import React, { useRef, forwardRef, useImperativeHandle, useEffect, useState } from 'react';
import { cls, nextTick, componentWrapper } from '@arco-design/mobile-utils';
import { ContextLayout } from '../context-provider';
import Portal from '../portal';
import Transition from '../transition';
import { usePopupScroll, usePreventBodyScroll } from '../_helpers';
import { open } from './methods';
export * from './methods';
var Masking = /*#__PURE__*/forwardRef(function (props, ref) {
  var className = props.className,
      _props$visible = props.visible,
      visible = _props$visible === void 0 ? false : _props$visible,
      maskClass = props.maskClass,
      maskStyle = props.maskStyle,
      contentClass = props.contentClass,
      contentStyle = props.contentStyle,
      close = props.close,
      onClose = props.onClose,
      onOpen = props.onOpen,
      onMaskClick = props.onMaskClick,
      children = props.children,
      _props$maskTransition = props.maskTransitionType,
      maskTransitionType = _props$maskTransition === void 0 ? 'fade' : _props$maskTransition,
      _props$contentTransit = props.contentTransitionType,
      contentTransitionType = _props$contentTransit === void 0 ? 'fade' : _props$contentTransit,
      _props$maskTransition2 = props.maskTransitionTimeout,
      maskTransitionTimeout = _props$maskTransition2 === void 0 ? 300 : _props$maskTransition2,
      _props$contentTransit2 = props.contentTransitionTimeout,
      contentTransitionTimeout = _props$contentTransit2 === void 0 ? 300 : _props$contentTransit2,
      _props$maskClosable = props.maskClosable,
      maskClosable = _props$maskClosable === void 0 ? true : _props$maskClosable,
      animatingClosable = props.animatingClosable,
      _props$mountOnEnter = props.mountOnEnter,
      mountOnEnter = _props$mountOnEnter === void 0 ? true : _props$mountOnEnter,
      _props$unmountOnExit = props.unmountOnExit,
      unmountOnExit = _props$unmountOnExit === void 0 ? true : _props$unmountOnExit,
      _props$orientationDir = props.orientationDirection,
      orientationDirection = _props$orientationDir === void 0 ? 'top' : _props$orientationDir,
      _props$preventBodyScr = props.preventBodyScroll,
      preventBodyScroll = _props$preventBodyScr === void 0 ? true : _props$preventBodyScr,
      initialBodyOverflow = props.initialBodyOverflow,
      _props$contentAtCente = props.contentAtCenter,
      contentAtCenter = _props$contentAtCente === void 0 ? false : _props$contentAtCente,
      _props$gestureOutOfCo = props.gestureOutOfControl,
      gestureOutOfControl = _props$gestureOutOfCo === void 0 ? false : _props$gestureOutOfCo,
      getContainer = props.getContainer,
      getScrollContainer = props.getScrollContainer,
      onPreventTouchMove = props.onPreventTouchMove,
      onTouchMove = props.onTouchMove;
  var domRef = useRef(null);
  var maskRef = useRef(null);
  var contentRef = useRef(null);
  var timerRef = useRef(-1);
  var isInitialMount = useRef(false);
  var isAnimating = useRef(false);
  var closeScene = useRef('');

  var _useState = useState(visible),
      innerVisible = _useState[0],
      setInnerVisible = _useState[1];

  var _useState2 = useState(visible),
      outerVisible = _useState2[0],
      setOuterVisible = _useState2[1];

  var _useState3 = useState(''),
      extraClass = _useState3[0],
      setExtraClass = _useState3[1];

  useImperativeHandle(ref, function () {
    return {
      dom: domRef.current,
      mask: maskRef.current,
      content: contentRef.current,
      setCloseScene: setCloseScene
    };
  });
  usePopupScroll(innerVisible, domRef.current, getScrollContainer, orientationDirection, onPreventTouchMove, onTouchMove, gestureOutOfControl);
  usePreventBodyScroll(visible, preventBodyScroll, initialBodyOverflow);
  useEffect(function () {
    if (timerRef.current >= 0) {
      clearTimeout(timerRef.current);
    }

    var mounted = isInitialMount.current;

    if (mounted) {
      isAnimating.current = true;
    }

    if (!mounted && !visible && !mountOnEnter) {
      setExtraClass('pre-mount');
    }

    if (visible) {
      setOuterVisible(true);
      nextTick(function () {
        setInnerVisible(true);
        execAfterOpen(function () {
          isAnimating.current = false;
          setExtraClass('');
          mounted && (onOpen == null ? void 0 : onOpen());
        });
      });
    } else {
      setInnerVisible(false);
      timerRef.current = execAfterClose(function () {
        isAnimating.current = false;
        setOuterVisible(false);
        mounted && (onClose == null ? void 0 : onClose(closeScene.current || void 0));
        closeScene.current = '';
      });
    }

    isInitialMount.current = true;
  }, [visible]);

  function setCloseScene(scene) {
    closeScene.current = scene;
  }

  function execAfterClose(callback) {
    return window.setTimeout(callback, Math.max(getMaxTimeout(maskTransitionTimeout, 'exit'), getMaxTimeout(contentTransitionTimeout, 'exit')));
  }

  function execAfterOpen(callback) {
    return window.setTimeout(callback, Math.max(getMaxTimeout(maskTransitionTimeout, 'enter'), getMaxTimeout(contentTransitionTimeout, 'enter')));
  }

  function getMaxTimeout(timeout, type) {
    return typeof timeout === 'number' ? timeout : timeout[type] || 0;
  }

  function handleMaskAction(e) {
    e.preventDefault();
    e.stopPropagation();
    onMaskClick == null ? void 0 : onMaskClick();

    if (!maskClosable || !animatingClosable && isAnimating.current) {
      return;
    }

    closeScene.current = 'mask';
    close == null ? void 0 : close(e);
  }

  function renderMasking(_ref) {
    var prefixCls = _ref.prefixCls;
    return !mountOnEnter || outerVisible || !unmountOnExit ? /*#__PURE__*/React.createElement(Portal, {
      getContainer: getContainer
    }, /*#__PURE__*/React.createElement("div", {
      className: cls(prefixCls + "-masking", 'all-border-box', className),
      ref: domRef
    }, /*#__PURE__*/React.createElement(Transition, {
      in: innerVisible,
      timeout: maskTransitionTimeout,
      type: maskTransitionType,
      mountOnEnter: mountOnEnter,
      unmountOnExit: unmountOnExit
    }, /*#__PURE__*/React.createElement("div", {
      ref: maskRef,
      className: cls(prefixCls + "-masking-mask", extraClass, maskClass),
      onClick: handleMaskAction,
      style: maskStyle
    })), /*#__PURE__*/React.createElement(Transition, {
      in: innerVisible,
      timeout: contentTransitionTimeout,
      type: contentTransitionType,
      mountOnEnter: mountOnEnter,
      unmountOnExit: unmountOnExit
    }, /*#__PURE__*/React.createElement("div", {
      ref: contentRef,
      className: cls(prefixCls + "-masking-content", {
        'at-center': contentAtCenter
      }, extraClass, contentClass),
      style: contentStyle
    }, children)))) : null;
  }

  return /*#__PURE__*/React.createElement(ContextLayout, null, renderMasking);
});
export function methodsGenerator(Comp) {
  return {
    /**
     * 打开模态弹窗
     * @desc {en} Open a Masking
     * @param {MaskingProps} config Configuration
     * @returns {{ close: () => void; update: (newConfig: MaskingProps) => void; }}
     */
    open: open(Comp)
  };
}
/**
 * 通用模态弹窗，内部内容自定义。默认做了防滚动穿透处理，如果弹层内容中需要滚动，则需将滚动容器传入`getScrollContainer`属性以在未滚动到顶部或底部时释放滚动。
 * @en Generic modal popup with custom internal content. By default, anti-scroll penetration processing is performed. If scrolling is required in the content of the bullet layer, need to pass the scroll container to the `getScrollContainer` to release scrolling when it is not scrolled to the top or bottom.
 * @type 反馈
 * @type_en Feedback
 * @name 模态弹窗
 * @name_en Masking
 */

export default componentWrapper(Masking, methodsGenerator(Masking));